import React, {useCallback, useMemo, useState} from 'react';

import {DateRangeInput, FormInput, NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {updateProductAccessTerm} from 'src/openapi/admin/apis/ProductSubscriptionApi';
import {UpdateProductAccessTermRequestStatus} from 'src/openapi/admin/models/UpdateProductAccessTermRequest';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';

import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

type Props = {
    subscription: ProductSubscription,
    onBusyChange: (busy: boolean) => void,
    onClose: () => void,
    onUpdated: () => void,
};

const SUCCESS_CLOSE_DELAY = 4000;

export const EditAccessTermModal: React.FC<Props> = (props: Props) => {
    const {subscription, onBusyChange, onClose, onUpdated} = props;
    const apiDispatch = useAppDispatch();
    const initialReferenceId = subscription.sourceRefs[0]?.referenceId || '';
    const [status, setStatus] = useState<UpdateProductAccessTermRequestStatus>(
        UpdateProductAccessTermRequestStatus.ACTIVE,
    );
    const [referenceId, setReferenceId] = useState(initialReferenceId);
    const [startDate, setStartDate] = useState(subscription.startDate);
    const [endDate, setEndDate] = useState(subscription.endDate);
    const [seats, setSeats] = useState<number | undefined>(
        subscription.seats === 2147483647 ? undefined : subscription.seats || 0,
    );
    const [serverError, setServerError] = useState('');
    const [busy, setBusy] = useState(false);

    const hasInvalidRange = Boolean(startDate && endDate && startDate > endDate);
    const hasInvalidSeats = seats !== undefined && seats < 1;
    const isChanged = status !== UpdateProductAccessTermRequestStatus.ACTIVE || referenceId !== initialReferenceId ||
        startDate !== subscription.startDate || endDate !== subscription.endDate || seats !== subscription.seats;
    const canUpdate = Boolean(isChanged && startDate && endDate && !hasInvalidRange && !hasInvalidSeats);

    const sourceRefs = useMemo(() => {
        if (referenceId === initialReferenceId) {
            return subscription.sourceRefs;
        }

        return referenceId ? [{referenceId, type: 'Contract ID'}] : [];
    }, [initialReferenceId, referenceId, subscription.sourceRefs]);

    const onCancel = useCallback(() => {
        if (!busy) {
            onClose();
        }
    }, [busy, onClose]);

    const onUpdate = useCallback(async () => {
        if (!canUpdate || busy) {
            return;
        }

        setBusy(true);
        onBusyChange(true);
        setServerError('');

        const updateProductAccessTermRequest = {
            status,
            startDate,
            endDate,
            ...(seats !== undefined ? {seats} : {}),
            sourceRefs,
        };
        const response = await apiDispatch(updateProductAccessTerm({
            productSubscriptionId: subscription.productSubscriptionId,
            updateProductAccessTermRequest,
        }));

        if (!response.payload?.success) {
            setServerError(getError({error: response.payload?.data, separator: '. '}));
            setBusy(false);
            onBusyChange(false);
            return;
        }

        await new Promise(resolve => setTimeout(resolve, SUCCESS_CLOSE_DELAY));
        onUpdated();
    }, [
        apiDispatch, busy, canUpdate, endDate, onBusyChange, onUpdated, seats, sourceRefs, startDate, status,
        subscription.productSubscriptionId,
    ]);

    return (
        <>
            <DialogHeader onClose={onCancel}>Update Product Access Term</DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="l">
                    {serverError ? <Message color="error">{serverError}</Message> : null}
                    <Item>
                        <Label><RequiredMark label="Product"/></Label>
                        <ComboBox value={subscription.product.externalKey} disabled={true} onChange={() => undefined}>
                            <ComboboxItem value={subscription.product.externalKey} caption={subscription.product.name}/>
                        </ComboBox>
                    </Item>
                    <Item>
                        <Label><RequiredMark label="Status"/></Label>
                        <ComboBox
                            value={status}
                            disabled={busy}
                            onChange={value => setStatus(value as UpdateProductAccessTermRequestStatus)}>
                            <ComboboxItem value={UpdateProductAccessTermRequestStatus.ACTIVE} caption="Active"/>
                            <ComboboxItem value={UpdateProductAccessTermRequestStatus.CANCEL} caption="Cancel"/>
                        </ComboBox>
                    </Item>
                    <FormInput
                        label="Contract ID"
                        placeholder="Enter contract ID"
                        value={referenceId}
                        maxLength={255}
                        disabled={busy}
                        onChange={setReferenceId}
                    />
                    <Group gap="l" alignItems="start">
                        <DateRangeInput
                            width={210}
                            id="product-access-term-start-date"
                            label={<RequiredMark label="Start Date"/>}
                            preventPastDate={true}
                            selectRangeType="start"
                            startDate={startDate}
                            endDate={endDate}
                            disabled={busy}
                            clearable={true}
                            onChange={setStartDate}
                        />
                        <DateRangeInput
                            width={210}
                            id="product-access-term-end-date"
                            label={<RequiredMark label="End Date"/>}
                            preventPastDate={true}
                            selectRangeType="end"
                            startDate={startDate}
                            endDate={endDate}
                            disabled={busy}
                            clearable={true}
                            onChange={setEndDate}
                        />
                    </Group>
                    {hasInvalidRange ? <Message color="error">End date cannot be before the start date</Message> : null}
                    <NumberInput
                        label="Seats"
                        value={seats}
                        displayedValue={seats === 2147483647 ? 'Unlimited' : seats?.toString() || ''}
                        placeholder="Unlimited"
                        inputType={NumberInputTypes.POSITIVE_INTEGER}
                        minValue={1}
                        maxValue={seats === 2147483647 ? undefined : 999999}
                        disabled={busy}
                        onChange={setSeats}
                        onBlur={setSeats}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} disabled={!canUpdate || busy} onClick={onUpdate}>Update</Button>
                    <Button variant="Type.SECONDARY" busy={busy} disabled={busy} onClick={onCancel}>Cancel</Button>
                </Group>
            </DialogFooter>
        </>
    );
};
